import LocationOn from '@ixigo/icons/LocationOnFilled';
import * as Timeline from '@ixigo/iui/Timeline';
import Typography from '@ixigo/iui/Typography';
import { Skeleton } from '@repo/shared/components';
import { CabFilled } from '@repo/shared/icons';
import { useEffect } from 'react';
import { SectionHeader } from '@/components/section-header';
import LoadingGif from '../-assets/booking-loading.gif';
import { BookingHeader } from './trip-detail-content';

async function preload(): Promise<void> {
	// Preload the GIF by creating an image element
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve();
		img.onerror = () => reject(new Error('Failed to load GIF'));
		img.src = LoadingGif;
	});
}

interface LoadingProps {
	confirmation?: boolean;
	bookingId?: string;
}

const ConfirmationLoading = () => {
	useEffect(() => {
		// Preload the GIF when component mounts
		preload().catch(console.error);
	}, []);
	return (
		<div className="translate-y-[16dvh]">
			<img src={LoadingGif} alt="loading" width={400} height={400} />
			<div className="animation-container w-full px-30 py-15">
				<Typography
					data-testid="loader-msg"
					variant="body-lg"
					className="text-primary text-center mx-15"
				>
					We are processing your booking...
				</Typography>
			</div>
		</div>
	);
};

const TripDetailLoading = ({ bookingId }: LoadingProps) => {
	const timelineItems = [
		{
			location: 'Loading...',
			icon: <LocationOn className="size-20 shrink-0 text-secondary" />,
		},
		{
			location: 'Loading...',
			icon: <CabFilled className="size-20 shrink-0 text-secondary" />,
		},
		{
			location: 'Loading...',
			icon: <LocationOn className="size-20 shrink-0 text-secondary" />,
		},
	];
	return (
		<div className="w-full flex flex-col justify-start items-center bg-tertiary">
			<header className="sticky top-0 z-10 border-b border-tertiary px-20 py-15 w-full flex justify-center items-center bg-primary">
				<Skeleton
					loading={true}
					variant="rectangular"
					className="w-full rounded-5"
					level="body-xs"
					height={24}
					width={24}
				/>
				<div className="w-full flex flex-col justify-center items-center">
					<Skeleton
						loading={true}
						variant="rectangular"
						className="w-full rounded-5"
						level="body-xs"
						height={24}
						width={200}
					/>
				</div>
			</header>
			<div className="w-full flex flex-col gap-y-10 items-start justify-between">
				<div className="flex flex-col gap-y-10 bg-primary p-20 w-full">
					<BookingHeader bookingId={bookingId ?? ''} loading={true} />
					<div className="flex justify-around items-center border border-tertiary rounded-20 p-10 w-full">
						{Array.from({ length: 3 }).map((_, index) => (
							<div key={index} className="flex flex-col items-center gap-2">
								<Skeleton
									loading={true}
									variant="rectangular"
									className="w-full rounded-5"
									level="body-xs"
									height={40}
									width={40}
								/>
								<Skeleton
									loading={true}
									variant="text"
									className="w-full"
									level="body-xs"
									height={16}
									width={64}
								/>
								<Skeleton
									loading={true}
									variant="text"
									className="w-full"
									level="body-xs"
									height={16}
									width={64}
								/>
							</div>
						))}
					</div>
				</div>
				<div className="bg-primary p-20 flex flex-col gap-y-15 w-full">
					<SectionHeader title="Booking Details" />
					<div className="flex justify-around items-center border border-tertiary rounded-[15px] w-full overflow-hidden">
						<div className="w-full flex flex-col">
							<div className="flex flex-col gap-y-10 p-15">
								<Skeleton
									loading={true}
									variant="text"
									className="w-full rounded-5"
									level="h6"
								/>
								<Timeline.Root
									orientation="vertical"
									contentPosition="end"
									className="w-full overflow-hidden"
								>
									{timelineItems.map((item, index) => (
										<Timeline.Item key={index} className="relative">
											<Timeline.Separator
												indicator={item.icon}
												connector={
													<div
														className={`w-px h-full border-x border-secondary ${
															index === timelineItems.length - 1 ? 'hidden' : ''
														}`}
													/>
												}
											/>
											<Timeline.Content className="overflow-hidden">
												<div className="w-full flex flex-col gap-y-0.5">
													<Skeleton
														loading={true}
														level="body-sm"
														height={16}
														variant="text"
														className="w-10/12 rounded-5"
													/>
													<Skeleton
														loading={true}
														level="body-xs"
														height={12}
														className="w-8/12 rounded-5"
														variant="text"
													/>
												</div>
											</Timeline.Content>
										</Timeline.Item>
									))}
								</Timeline.Root>
							</div>
							<div className="w-full flex justify-between gap-x-10 items-center py-10 px-15 bg-secondary">
								<Skeleton
									loading={true}
									height={40}
									width={40}
									variant="rectangular"
									className="rounded-lg shrink-0"
								/>
								<div className="w-full flex flex-col gap-y-2.5 h-full">
									<Skeleton
										loading={true}
										level="body-md"
										height={16}
										className="rounded-5"
										variant="text"
									/>
									<Skeleton
										loading={true}
										level="body-xs"
										height={12}
										className="rounded-5"
										variant="text"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const TripPageLoading = ({ confirmation = false, bookingId }: LoadingProps) => {
	return (
		<div className="h-screen w-screen flex flex-col justify-start items-center">
			{confirmation ? (
				<ConfirmationLoading />
			) : (
				<TripDetailLoading bookingId={bookingId} />
			)}
		</div>
	);
};

export default TripPageLoading;
